import React, { useState, useEffect } from "react";
import CallIcon from "@mui/icons-material/Call";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
  Container,
  CssBaseline,
  Menu,
  MenuItem,
  Box,
  Button,
  Divider,
} from "@mui/material";
import { Link as ScrollLink } from "react-scroll";
import { useLocation, useNavigate } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import TimelineIcon from "@mui/icons-material/Timeline";
import ApartmentIcon from "@mui/icons-material/Apartment";
import VerifiedIcon from "@mui/icons-material/Verified";
import TerminalIcon from "@mui/icons-material/Terminal";
import zealous from "../Images/Zealous.png";
import { brand } from "../theme";
import useDisableCopyPaste from "./useDisableCopyPaste";

function Navigation() {
  useDisableCopyPaste();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const navigate = useNavigate();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleDrawerToggle = () => setDrawerOpen(!drawerOpen);

  const scrollToTop = () => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    setDrawerOpen(false);
  };

  const handleNavClick = (to, isRoute = false) => {
    if (isRoute) {
      navigate(to);
    } else if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const element = document.getElementById(to);
        if (element) element.scrollIntoView({ behavior: "smooth" });
      }, 120);
    }
    setDrawerOpen(false);
  };

  const navLinks = [
    { to: "home-section", label: "Home" },
    { to: "about-section", label: "About" },
    { to: "services-section", label: "Services" },
    { to: "training-section", label: "Technology" },
    { to: "products-section", label: "Products" },
  ];

  const submenuLinks = [
    { to: "portfolio-section", label: "Training Portfolio", icon: <WorkspacePremiumIcon fontSize="small" /> },
    { to: "casestudies-section", label: "Case Studies", icon: <AutoGraphIcon fontSize="small" /> },
    { to: "roadmap-section", label: "Roadmap", icon: <TimelineIcon fontSize="small" /> },
    { to: "campus-section", label: "Campuses", icon: <ApartmentIcon fontSize="small" /> },
  ];

  const routeLinks = [
    { to: "/verify", label: "Verify Certificate", icon: <VerifiedIcon fontSize="small" /> },
    { to: "/compiler", label: "Compiler", icon: <TerminalIcon fontSize="small" /> },
  ];

  const handleSubMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleSubMenuClose = () => setAnchorEl(null);

  const linkSx = {
    cursor: "pointer",
    color: brand.navy,
    fontWeight: 600,
    fontSize: "0.9rem",
    letterSpacing: "0.015em",
    px: 1.25,
    py: 1,
    borderRadius: 2,
    position: "relative",
    transition: "color 0.2s ease",
    "&::after": {
      content: '""',
      position: "absolute",
      left: 12,
      right: 12,
      bottom: 6,
      height: 2,
      borderRadius: 2,
      background: `linear-gradient(90deg, ${brand.blue}, ${brand.orange})`,
      transform: "scaleX(0)",
      transformOrigin: "left",
      transition: "transform 0.28s cubic-bezier(0.22,1,0.36,1)",
    },
    "&:hover": { color: brand.blue },
    "&:hover::after": { transform: "scaleX(1)" },
  };

  // Shared premium styling for the "More" dropdown items
  const menuItemSx = {
    mx: 1,
    my: 0.25,
    px: 1.5,
    py: 1.1,
    borderRadius: 2,
    gap: 1.25,
    color: brand.navy,
    fontWeight: 600,
    fontSize: "0.9rem",
    transition: "background .2s ease, color .2s ease, transform .2s ease",
    "& svg": { color: brand.blue, transition: "color .2s ease" },
    "&:hover": { bgcolor: "rgba(12,131,200,0.08)", color: brand.blue, transform: "translateX(4px)" },
  };

  const renderNavLinks = () => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      {navLinks.map((link, index) => (
        <ScrollLink
          key={index}
          to={link.to}
          duration={700}
          spy={true}
          smooth={true}
          onClick={() => handleNavClick(link.to)}
        >
          <Typography component="span" sx={linkSx}>
            {link.label}
          </Typography>
        </ScrollLink>
      ))}

      <Typography
        component="span"
        onClick={handleSubMenuOpen}
        sx={{
          ...linkSx,
          display: "inline-flex",
          alignItems: "center",
          color: Boolean(anchorEl) ? brand.blue : brand.navy,
          "& .more-arrow": {
            transition: "transform 0.25s ease",
            transform: Boolean(anchorEl) ? "rotate(180deg)" : "none",
          },
        }}
      >
        More <ArrowDropDownIcon fontSize="small" className="more-arrow" />
      </Typography>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleSubMenuClose}
        elevation={0}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          "& .MuiPaper-root": {
            mt: 1.5,
            minWidth: 252,
            borderRadius: 3,
            border: "1px solid rgba(12,131,200,0.14)",
            boxShadow: "0 20px 46px rgba(15,39,64,0.18)",
            overflow: "hidden",
            py: 1,
          },
        }}
      >
        <Typography
          sx={{
            px: 2.5,
            pt: 0.5,
            pb: 1,
            fontSize: "0.68rem",
            fontWeight: 800,
            letterSpacing: "0.14em",
            color: brand.slate,
          }}
        >
          EXPLORE MORE
        </Typography>
        {submenuLinks.map((link, index) => (
          <ScrollLink
            key={index}
            to={link.to}
            duration={700}
            spy={true}
            smooth={true}
            onClick={() => {
              handleNavClick(link.to);
              handleSubMenuClose();
            }}
          >
            <MenuItem sx={menuItemSx}>
              {link.icon}
              {link.label}
            </MenuItem>
          </ScrollLink>
        ))}
        <Divider sx={{ my: 1 }} />
        {routeLinks.map((link, index) => (
          <MenuItem
            key={index}
            sx={{ ...menuItemSx, color: brand.blue, "& svg": { color: brand.orange } }}
            onClick={() => {
              handleNavClick(link.to, true);
              handleSubMenuClose();
            }}
          >
            {link.icon}
            {link.label}
          </MenuItem>
        ))}
      </Menu>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, ml: 1.5 }}>
        <Box
          sx={{
            display: { lg: "flex", xl: "flex" },
            alignItems: "center",
            color: brand.orange,
            fontWeight: 700,
          }}
        >
          <CallIcon fontSize="small" sx={{ mr: 0.5 }} />
          <Typography component="span" sx={{ fontSize: "0.9rem", fontWeight: 700 }}>
            +91 95973 34782
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => handleNavClick("contacts-section")}
        >
          <ScrollLink to="contacts-section" smooth={true} duration={700} style={{ color: "inherit" }}>
            Reach Us
          </ScrollLink>
        </Button>
      </Box>
    </Box>
  );

  const renderDrawerLinks = () => (
    <List sx={{ pt: 0 }}>
      {navLinks.map((link, index) => (
        <ScrollLink
          key={index}
          to={link.to}
          duration={700}
          spy={true}
          smooth={true}
          onClick={() => handleNavClick(link.to)}
        >
          <ListItemButton sx={{ color: "#fff" }}>
            <ListItemText primary={link.label} primaryTypographyProps={{ fontWeight: 600 }} />
          </ListItemButton>
        </ScrollLink>
      ))}
      <Divider sx={{ borderColor: "rgba(255,255,255,0.2)", my: 1 }} />
      {submenuLinks.map((link, index) => (
        <ScrollLink
          key={index}
          to={link.to}
          duration={700}
          spy={true}
          smooth={true}
          onClick={() => handleNavClick(link.to)}
        >
          <ListItemButton sx={{ color: "rgba(255,255,255,0.9)" }}>
            <ListItemText primary={link.label} />
          </ListItemButton>
        </ScrollLink>
      ))}
      <Divider sx={{ borderColor: "rgba(255,255,255,0.2)", my: 1 }} />
      {routeLinks.map((link, index) => (
        <ListItemButton
          key={index}
          sx={{ color: "#fff" }}
          onClick={() => handleNavClick(link.to, true)}
        >
          <ListItemText primary={link.label} primaryTypographyProps={{ fontWeight: 600 }} />
        </ListItemButton>
      ))}
      <Box sx={{ px: 2, mt: 2 }}>
        <Button
          fullWidth
          variant="contained"
          color="secondary"
          onClick={() => handleNavClick("contacts-section")}
        >
          Reach Us
        </Button>
        <Box sx={{ display: "flex", alignItems: "center", color: "#fff", mt: 2 }}>
          <CallIcon fontSize="small" sx={{ mr: 1 }} />
          <Typography sx={{ fontSize: "0.9rem" }}>+91 95973 34782</Typography>
        </Box>
      </Box>
    </List>
  );

  return (
    <>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: scrolled ? "rgba(255,255,255,0.92)" : "#ffffff",
          backdropFilter: scrolled ? "saturate(180%) blur(10px)" : "none",
          borderBottom: "1px solid",
          borderColor: scrolled ? "rgba(12,131,200,0.12)" : "rgba(0,0,0,0.04)",
          boxShadow: scrolled ? "0 6px 24px rgba(15,39,64,0.08)" : "none",
          zIndex: theme.zIndex.drawer + 1,
          transition: "all 0.3s ease",
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Toolbar disableGutters sx={{ justifyContent: "space-between", minHeight: { xs: 64, md: 80 } }}>
            <img
              src={zealous}
              onClick={scrollToTop}
              style={{ cursor: "pointer", maxHeight: isSmallScreen ? 48 : 64 }}
              alt="Zealous Tech Corp"
            />
            {isMobile ? (
              <IconButton
                edge="end"
                aria-label="menu"
                onClick={handleDrawerToggle}
                sx={{ color: brand.blue }}
              >
                {drawerOpen ? <CloseIcon /> : <MenuIcon />}
              </IconButton>
            ) : (
              renderNavLinks()
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            background: `linear-gradient(160deg, ${brand.blueDark} 0%, ${brand.blue} 100%)`,
            width: 280,
            pt: 10,
          },
        }}
      >
        {renderDrawerLinks()}
      </Drawer>
      <Toolbar sx={{ minHeight: { xs: 64, md: 80 } }} />
    </>
  );
}

export default Navigation;
